import { useState, useEffect } from "react";
import { planningAPI, projectsAPI, uploadAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Upload, Save, X, FileImage, Search } from "lucide-react";

const roofTypes = [
  "Ziegeldach",
  "Flachdach",
  "Blechdach",
  "Schieferdach",
  "Bitumendach",
  "Trapezblech",
  "Eternit/Faserzement",
  "Sonstiges"
];

const cableRoutes = [
  "Außen",
  "Innen",
  "Schacht",
  "Kabelkanal",
  "Dachboden",
  "Fassade",
  "Sonstiges"
];

const hakLocations = [
  "Keller",
  "Erdgeschoss",
  "Außenwand",
  "Garage",
  "Technikraum",
  "Sonstiges"
];

const roofOrientations = [
  "Süd",
  "Süd-Ost",
  "Süd-West",
  "Ost",
  "West",
  "Ost-West (2 Flächen)",
  "Nord",
  "Flachdach (aufgeständert)"
];

const meterTypes = [
  "Drehstromzähler",
  "Eintarifzähler",
  "Zweirichtungszähler",
  "Smart Meter",
  "Ferraris (alt)",
  "Sonstiges"
];

const groundingTypes = [
  "Fundamenterder",
  "Erdspieß",
  "Ringerder",
  "Anschluss vorhanden",
  "Keine vorhanden",
  "Sonstiges"
];

export default function PlanningPage() {
  const [projects, setProjects] = useState([]);
  const [plannings, setPlannings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeTab, setActiveTab] = useState("ac");
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  const [formData, setFormData] = useState({
    ac_planning: {
      // Zählerkasten
      new_meter_cabinet: "",
      meter_type: "",
      meter_cabinet_space: "",
      // HAK
      hak_location: "",
      hak_amperage: "",
      // Abstände
      distance_meter_to_inverter: "",
      distance_storage_to_inverter: "",
      ac_cable_route: "",
      // Erdung
      grounding_type: "",
      grounding_notes: "",
      // Fotos
      old_meter_cabinet_photo: null,
      hak_photo: null,
      inverter_location_photo: null,
      grounding_photo: null
    },
    dc_planning: {
      // Dach
      roof_type: "",
      roof_orientation: "",
      roof_angle: "",
      roof_condition: "",
      // Strings
      string_count: "",
      modules_per_string: "",
      // Kabel
      dc_cable_route: "",
      dc_cable_length: "",
      // Besonderheiten
      shading_notes: "",
      special_conditions: "",
      // Fotos
      roof_photo: null,
      module_plan_photo: null,
      cable_route_photo: null,
      shading_photo: null
    },
    general_notes: ""
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [projectsRes, planningsRes] = await Promise.all([
        projectsAPI.list(),
        planningAPI.list()
      ]);
      setProjects(projectsRes.data);
      setPlannings(planningsRes.data);
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPlanning = async (project) => {
    setSelectedProject(project);
    
    // Check if planning already exists
    const existingPlanning = plannings.find(p => p.project_id === project.id);
    
    if (existingPlanning) {
      setFormData({
        ac_planning: existingPlanning.ac_planning || {},
        dc_planning: existingPlanning.dc_planning || {},
        general_notes: existingPlanning.general_notes || ""
      });
    } else {
      setFormData({
        ac_planning: {
          new_meter_cabinet: "",
          meter_type: "",
          meter_cabinet_space: "",
          hak_location: "",
          hak_amperage: "",
          distance_meter_to_inverter: "",
          distance_storage_to_inverter: "",
          ac_cable_route: "",
          grounding_type: "",
          grounding_notes: "",
          old_meter_cabinet_photo: null,
          hak_photo: null,
          inverter_location_photo: null,
          grounding_photo: null
        },
        dc_planning: {
          roof_type: "",
          roof_orientation: "",
          roof_angle: "",
          roof_condition: "",
          string_count: "",
          modules_per_string: "",
          dc_cable_route: "",
          dc_cable_length: "",
          shading_notes: "",
          special_conditions: "",
          roof_photo: null,
          module_plan_photo: null,
          cable_route_photo: null,
          shading_photo: null
        },
        general_notes: ""
      });
    }
    
    setDialogOpen(true);
  };

  const handleFileUpload = async (e, field, section) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const response = await uploadAPI.upload(file);
      const fileUrl = uploadAPI.getFileUrl(response.data.file_id);
      
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: fileUrl
        }
      }));
      
      toast.success("Foto hochgeladen");
    } catch (error) {
      console.error("Failed to upload file:", error);
      toast.error("Fehler beim Hochladen");
    }
  };

  const handleSave = async () => {
    if (!selectedProject) return;
    
    setSaving(true);
    
    try {
      const existingPlanning = plannings.find(p => p.project_id === selectedProject.id);
      
      if (existingPlanning) {
        await planningAPI.update(existingPlanning.id, {
          project_id: selectedProject.id,
          ...formData
        });
      } else {
        await planningAPI.create({
          project_id: selectedProject.id,
          ...formData
        });
      }
      
      toast.success("Planung gespeichert");
      setDialogOpen(false);
      loadData();
    } catch (error) {
      console.error("Failed to save planning:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const filteredProjects = projects.filter(project => {
    const searchLower = searchTerm.toLowerCase();
    return (
      project.project_number?.toLowerCase().includes(searchLower) ||
      project.customer_name?.toLowerCase().includes(searchLower)
    );
  });

  const getProjectPlanning = (projectId) => {
    return plannings.find(p => p.project_id === projectId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="planning-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-['Manrope']">Planung</h1>
        <p className="text-muted-foreground">
          AC- und DC-Planung für Projekte vor Ort
        </p>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Suche nach Projekt oder Kunde..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 h-12"
          data-testid="planning-search"
        />
      </div>

      {/* Projects List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredProjects.map((project) => {
          const planning = getProjectPlanning(project.id);
          const hasPlanning = !!planning;
          
          return (
            <Card 
              key={project.id} 
              className="card-hover cursor-pointer"
              onClick={() => handleOpenPlanning(project)}
              data-testid={`planning-card-${project.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-mono text-sm text-muted-foreground">
                      {project.project_number}
                    </p>
                    <p className="font-medium mt-1">{project.customer_name}</p>
                  </div>
                  <Badge variant={hasPlanning ? "default" : "secondary"}>
                    {hasPlanning ? "Geplant" : "Offen"}
                  </Badge>
                </div>
                
                {hasPlanning && (
                  <div className="mt-3 pt-3 border-t space-y-1 text-sm text-muted-foreground">
                    {planning.ac_planning?.new_meter_cabinet && (
                      <p>ZK: {planning.ac_planning.new_meter_cabinet}</p>
                    )}
                    {planning.dc_planning?.roof_type && (
                      <p>Dach: {planning.dc_planning.roof_type}</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
        
        {filteredProjects.length === 0 && (
          <Card className="col-span-full py-12">
            <CardContent className="text-center">
              <FileImage className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-semibold mb-2">Keine Projekte gefunden</h3>
              <p className="text-muted-foreground">
                {searchTerm ? "Versuchen Sie eine andere Suche" : "Erstellen Sie zuerst ein Kundenprojekt"}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Planning Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              Planung: {selectedProject?.project_number} - {selectedProject?.customer_name}
            </DialogTitle>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="ac">AC Planung</TabsTrigger>
              <TabsTrigger value="dc">DC Planung</TabsTrigger>
              <TabsTrigger value="notes">Notizen</TabsTrigger>
            </TabsList>

            <TabsContent value="ac" className="space-y-4 mt-4">
              {/* Zählerkasten Section */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Zählerkasten</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Neuer ZK nötig</Label>
                      <Select
                        value={formData.ac_planning.new_meter_cabinet}
                        onValueChange={(value) => setFormData(prev => ({
                          ...prev,
                          ac_planning: { ...prev.ac_planning, new_meter_cabinet: value }
                        }))}
                      >
                        <SelectTrigger data-testid="new-meter-cabinet-select">
                          <SelectValue placeholder="Auswählen" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ja">Ja</SelectItem>
                          <SelectItem value="nein">Nein</SelectItem>
                          <SelectItem value="pruefen">Prüfen</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Zählertyp vorhanden</Label>
                      <Select
                        value={formData.ac_planning.meter_type}
                        onValueChange={(value) => setFormData(prev => ({
                          ...prev,
                          ac_planning: { ...prev.ac_planning, meter_type: value }
                        }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Auswählen" />
                        </SelectTrigger>
                        <SelectContent>
                          {meterTypes.map((type) => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Platz im ZK (Reihen frei)</Label>
                    <Input
                      value={formData.ac_planning.meter_cabinet_space}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        ac_planning: { ...prev.ac_planning, meter_cabinet_space: e.target.value }
                      }))}
                      placeholder="z.B. 2 Reihen frei"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* HAK Section */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Hausanschlusskasten (HAK)</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Standort HAK</Label>
                      <Select
                        value={formData.ac_planning.hak_location}
                        onValueChange={(value) => setFormData(prev => ({
                          ...prev,
                          ac_planning: { ...prev.ac_planning, hak_location: value }
                        }))}
                      >
                        <SelectTrigger data-testid="hak-location-select">
                          <SelectValue placeholder="Auswählen" />
                        </SelectTrigger>
                        <SelectContent>
                          {hakLocations.map((loc) => (
                            <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>HAK Absicherung</Label>
                      <Input
                        value={formData.ac_planning.hak_amperage}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          ac_planning: { ...prev.ac_planning, hak_amperage: e.target.value }
                        }))}
                        placeholder="z.B. 63A"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Abstände und Kabelwege */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Abstände & Kabelwege</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Abstand ZK zu WR</Label>
                      <Input
                        value={formData.ac_planning.distance_meter_to_inverter}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          ac_planning: { ...prev.ac_planning, distance_meter_to_inverter: e.target.value }
                        }))}
                        placeholder="z.B. 10m"
                        data-testid="distance-meter-inverter"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Abstand Speicher zu WR</Label>
                      <Input
                        value={formData.ac_planning.distance_storage_to_inverter}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          ac_planning: { ...prev.ac_planning, distance_storage_to_inverter: e.target.value }
                        }))}
                        placeholder="z.B. 2m"
                        data-testid="distance-storage-inverter"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>AC-Kabelweg</Label>
                    <Select
                      value={formData.ac_planning.ac_cable_route}
                      onValueChange={(value) => setFormData(prev => ({
                        ...prev,
                        ac_planning: { ...prev.ac_planning, ac_cable_route: value }
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {cableRoutes.map((route) => (
                          <SelectItem key={route} value={route}>{route}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Erdung */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Erdung / Potentialausgleich</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Erdungsart</Label>
                    <Select
                      value={formData.ac_planning.grounding_type}
                      onValueChange={(value) => setFormData(prev => ({
                        ...prev,
                        ac_planning: { ...prev.ac_planning, grounding_type: value }
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {groundingTypes.map((type) => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Bemerkungen Erdung</Label>
                    <Input
                      value={formData.ac_planning.grounding_notes}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        ac_planning: { ...prev.ac_planning, grounding_notes: e.target.value }
                      }))}
                      placeholder="z.B. Blitzschutz vorhanden"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Photo Uploads - AC */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Fotos AC</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { key: "old_meter_cabinet_photo", label: "Zählerkasten" },
                      { key: "hak_photo", label: "HAK" },
                      { key: "inverter_location_photo", label: "Standort WR/Batterie" },
                      { key: "grounding_photo", label: "Erdung/PA" }
                    ].map((item) => (
                      <div key={item.key} className="space-y-2">
                        <Label className="text-xs">{item.label}</Label>
                        <div className="border-2 border-dashed rounded-lg p-3 text-center hover:border-primary transition-colors">
                          {formData.ac_planning[item.key] ? (
                            <div className="space-y-2">
                              <img
                                src={formData.ac_planning[item.key]}
                                alt={item.label}
                                className="w-full h-20 object-cover rounded"
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setFormData(prev => ({
                                  ...prev,
                                  ac_planning: { ...prev.ac_planning, [item.key]: null }
                                }))}
                              >
                                <X className="h-3 w-3 mr-1" />
                                Entfernen
                              </Button>
                            </div>
                          ) : (
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleFileUpload(e, item.key, "ac_planning")}
                              />
                              <Upload className="h-6 w-6 mx-auto mb-1 text-muted-foreground" />
                              <p className="text-xs text-muted-foreground">Hochladen</p>
                            </label>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="dc" className="space-y-4 mt-4">
              {/* Dach Section */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Dach</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Dachart</Label>
                      <Select
                        value={formData.dc_planning.roof_type}
                        onValueChange={(value) => setFormData(prev => ({
                          ...prev,
                          dc_planning: { ...prev.dc_planning, roof_type: value }
                        }))}
                      >
                        <SelectTrigger data-testid="roof-type-select">
                          <SelectValue placeholder="Auswählen" />
                        </SelectTrigger>
                        <SelectContent>
                          {roofTypes.map((type) => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Ausrichtung</Label>
                      <Select
                        value={formData.dc_planning.roof_orientation}
                        onValueChange={(value) => setFormData(prev => ({
                          ...prev,
                          dc_planning: { ...prev.dc_planning, roof_orientation: value }
                        }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Auswählen" />
                        </SelectTrigger>
                        <SelectContent>
                          {roofOrientations.map((orient) => (
                            <SelectItem key={orient} value={orient}>{orient}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Dachneigung</Label>
                      <Input
                        value={formData.dc_planning.roof_angle}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          dc_planning: { ...prev.dc_planning, roof_angle: e.target.value }
                        }))}
                        placeholder="z.B. 35°"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Dachzustand</Label>
                      <Select
                        value={formData.dc_planning.roof_condition}
                        onValueChange={(value) => setFormData(prev => ({
                          ...prev,
                          dc_planning: { ...prev.dc_planning, roof_condition: value }
                        }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Auswählen" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="gut">Gut</SelectItem>
                          <SelectItem value="mittel">Mittel</SelectItem>
                          <SelectItem value="sanierung_noetig">Sanierung nötig</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* String-Belegung */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">String-Belegung</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Anzahl Strings</Label>
                      <Input
                        value={formData.dc_planning.string_count}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          dc_planning: { ...prev.dc_planning, string_count: e.target.value }
                        }))}
                        placeholder="z.B. 2"
                        type="number"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Module pro String</Label>
                      <Input
                        value={formData.dc_planning.modules_per_string}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          dc_planning: { ...prev.dc_planning, modules_per_string: e.target.value }
                        }))}
                        placeholder="z.B. 10, 12"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* DC Kabel */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">DC-Verkabelung</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Leitungsweg DC-Kabel</Label>
                      <Select
                        value={formData.dc_planning.dc_cable_route}
                        onValueChange={(value) => setFormData(prev => ({
                          ...prev,
                          dc_planning: { ...prev.dc_planning, dc_cable_route: value }
                        }))}
                      >
                        <SelectTrigger data-testid="dc-cable-route-select">
                          <SelectValue placeholder="Auswählen" />
                        </SelectTrigger>
                        <SelectContent>
                          {cableRoutes.map((route) => (
                            <SelectItem key={route} value={route}>{route}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Länge DC-Kabel</Label>
                      <Input
                        value={formData.dc_planning.dc_cable_length}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          dc_planning: { ...prev.dc_planning, dc_cable_length: e.target.value }
                        }))}
                        placeholder="z.B. 25m"
                        data-testid="dc-cable-length"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Besonderheiten */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Besonderheiten</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Verschattung</Label>
                    <Input
                      value={formData.dc_planning.shading_notes}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        dc_planning: { ...prev.dc_planning, shading_notes: e.target.value }
                      }))}
                      placeholder="z.B. Baum im Süden, Schornstein"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Sonstige Besonderheiten</Label>
                    <Input
                      value={formData.dc_planning.special_conditions}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        dc_planning: { ...prev.dc_planning, special_conditions: e.target.value }
                      }))}
                      placeholder="z.B. Dachfenster, Gauben, Antenne"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Photo Uploads - DC */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Fotos DC</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { key: "roof_photo", label: "Dachaufnahme" },
                      { key: "module_plan_photo", label: "Modulplan/Belegung" },
                      { key: "cable_route_photo", label: "Kabelweg" },
                      { key: "shading_photo", label: "Verschattung" }
                    ].map((item) => (
                      <div key={item.key} className="space-y-2">
                        <Label className="text-xs">{item.label}</Label>
                        <div className="border-2 border-dashed rounded-lg p-3 text-center hover:border-primary transition-colors">
                          {formData.dc_planning[item.key] ? (
                            <div className="space-y-2">
                              <img
                                src={formData.dc_planning[item.key]}
                                alt={item.label}
                                className="w-full h-20 object-cover rounded"
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setFormData(prev => ({
                                  ...prev,
                                  dc_planning: { ...prev.dc_planning, [item.key]: null }
                                }))}
                              >
                                <X className="h-3 w-3 mr-1" />
                                Entfernen
                              </Button>
                            </div>
                          ) : (
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleFileUpload(e, item.key, "dc_planning")}
                              />
                              <Upload className="h-6 w-6 mx-auto mb-1 text-muted-foreground" />
                              <p className="text-xs text-muted-foreground">Hochladen</p>
                            </label>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="notes" className="space-y-4 mt-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Allgemeine Notizen</CardTitle>
                  <CardDescription>Zusätzliche Bemerkungen zur Vor-Ort-Planung</CardDescription>
                </CardHeader>
                <CardContent>
                  <textarea
                    className="w-full min-h-[200px] p-3 border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                    value={formData.general_notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, general_notes: e.target.value }))}
                    placeholder="Weitere Notizen, Besonderheiten, Absprachen mit dem Kunden..."
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-planning-btn">
              {saving ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-2 border-primary-foreground border-t-transparent" />
                  Speichern...
                </span>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Speichern
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
